import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { TIPOS, TIPO_ICON, COLORES, colorDe } from './tipos'

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'

// Options typed one per line (or comma-separated) -> clean list.
const parseOpciones = (text) => {
  const seen = new Set()
  return text.split(/[\n,]/).map(s => s.trim()).filter(s => {
    if (!s || seen.has(s.toLowerCase())) return false
    seen.add(s.toLowerCase()); return true
  })
}

// "+ Agregar columna": name, type and (for estado) options. The column
// belongs to the Planilla's project -- there are no common columns -- or,
// with ambito "partes", to the Partes board (which has no project).
export function AddColumnaModal({ proyecto, ambito = 'unidades', onClose, onCreate }) {
  const { t } = useTranslation('engineering')
  const [nombre, setNombre] = useState('')
  const [tipo, setTipo] = useState('texto')
  const [opciones, setOpciones] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    const data = ambito === 'partes'
      ? { nombre: nombre.trim(), tipo, ambito }
      : { nombre: nombre.trim(), tipo, proyecto }
    if (!data.nombre) { setError(t('unidades.planilla.nameRequired')); return }
    if (tipo === 'estado') {
      data.opciones = parseOpciones(opciones)
      if (!data.opciones.length) { setError(t('unidades.planilla.optionsRequired')); return }
    }
    setSaving(true)
    try {
      await onCreate(data)
      onClose()
    } catch (err) {
      const d = err.response?.data?.detail
      setError(err.response?.status === 409
        ? t('unidades.planilla.alreadyExists', { nombre: data.nombre })
        : d?.code === 'proyecto_desconocido' ? (d.message || t('unidades.proyectoDesconocido'))
        : t('unidades.planilla.columnSaveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={t('unidades.planilla.addColumnTitle')} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.planilla.columnName')} *</label>
          <input autoFocus className={inputCls} value={nombre} onChange={e => setNombre(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.planilla.columnType')} *</label>
          <div className="grid grid-cols-5 gap-1.5">
            {TIPOS.map(tp => (
              <button key={tp} type="button" onClick={() => setTipo(tp)}
                className={`flex flex-col items-center gap-0.5 px-2 py-2 rounded-lg border text-xs transition-colors ${
                  tipo === tp ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <span className="text-sm leading-none">{TIPO_ICON[tp]}</span>
                {t(`unidades.planilla.tipos.${tp}`)}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1.5">{t(`unidades.planilla.tipoHints.${tipo}`)}</p>
        </div>
        {tipo === 'estado' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.planilla.options')} *</label>
            <textarea rows={4} className={inputCls} value={opciones}
              placeholder={t('unidades.planilla.optionsPlaceholder')}
              onChange={e => setOpciones(e.target.value)} />
          </div>
        )}
        <p className="text-xs text-gray-400">
          {ambito === 'partes' ? t('partes.columnHint') : t('unidades.planilla.projectOnlyHint', { proyecto })}
        </p>
        {error && <p className="text-red-500 text-xs">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            {t('unidades.cancel')}
          </button>
          <button type="submit" disabled={saving}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors">
            {saving ? t('unidades.saving') : t('unidades.planilla.createColumn')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// The ⋮ menu on a column header: change type, manage estado options, delete.
// Fixed-positioned from the button, so the table's horizontal scroll box
// can't clip it.
// "Ordenar": the two directions, worded for the column's type, and a way
// back to the load order. `dir` is the column's current one, if sorted by it.
export function OrdenBotones({ tipo, dir, onOrdenar }) {
  const { t } = useTranslation('engineering')
  const clase = tipo === 'numero' || tipo === 'archivo' ? 'numero' : tipo === 'fecha' ? 'fecha' : 'texto'
  const btn = (d) => `flex-1 px-2 py-1.5 rounded-md border text-xs font-medium transition-colors ${
    dir === d ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`
  return (
    <div>
      <p className="text-xs font-medium text-gray-700 mb-1">{t('unidades.orden.titulo')}</p>
      <div className="flex gap-1.5">
        <button type="button" className={btn('asc')} onClick={() => onOrdenar('asc')}>▲ {t(`unidades.orden.asc_${clase}`)}</button>
        <button type="button" className={btn('desc')} onClick={() => onOrdenar('desc')}>▼ {t(`unidades.orden.desc_${clase}`)}</button>
      </div>
      {dir && (
        <button type="button" onClick={() => onOrdenar(null)} className="mt-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-900">
          {t('unidades.orden.quitar')}
        </button>
      )}
    </div>
  )
}

// Popover anchored under a header button (fixed-positioned, like ColumnaMenu,
// so the table's scroll box can't clip it). Closes on outside click, Esc,
// scroll or resize.
function Popover({ anchor, onClose, ancho = 288, children }) {
  const ref = useRef(null)
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current?.contains(e.target) || e.target.closest?.('[data-columna-btn]')) return
      onClose()
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    const onMove = (e) => { if (!ref.current?.contains(e.target)) onClose() }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onClose)
    window.addEventListener('scroll', onMove, true)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onClose)
      window.removeEventListener('scroll', onMove, true)
    }
  }, [onClose])
  const style = { position: 'fixed', top: anchor.bottom + 4, left: Math.max(8, Math.min(anchor.left, window.innerWidth - ancho - 8)), width: ancho }
  return (
    <div ref={ref} style={style} className="z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-sm">
      {children}
    </div>
  )
}

// A fixed field's header (Código, N° parte, Estado...): its label, the sort
// arrow if the board is sorted by it, and on click a small menu to sort.
export function EncabezadoFijo({ label, campo, tipo = 'texto', orden, onOrdenar, className = '' }) {
  const [anchor, setAnchor] = useState(null)
  const dir = orden && String(orden.campo) === String(campo) ? orden.dir : null
  return (
    <>
      <button type="button" data-columna-btn
        onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setAnchor(a => a ? null : r) }}
        className={`inline-flex items-center gap-1 -mx-1.5 px-1.5 py-0.5 rounded-md transition-colors ${
          anchor ? 'bg-gray-200 text-gray-900' : 'hover:bg-gray-100'} ${className}`}>
        {label}
        {dir && <span className="text-[10px] text-gray-500">{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
      {anchor && (
        <Popover anchor={anchor} ancho={240} onClose={() => setAnchor(null)}>
          <OrdenBotones tipo={tipo} dir={dir} onOrdenar={d => { onOrdenar(campo, d); setAnchor(null) }} />
        </Popover>
      )}
    </>
  )
}

// `valoresEnUso`: the values this column shows in the current Planilla,
// proposed as options when it becomes an estado column. `orden`/`onOrdenar`:
// the board's sort (see ordenLogica.js), offered at the top of the menu.
// `soloLectura`: only the sort (no Mantenimiento editing).
export function ColumnaMenu({ columna, anchor, valoresEnUso = [], orden, onOrdenar, onClose, onUpdate, onRenombrar, onDelete, soloLectura = false }) {
  const { t } = useTranslation('engineering')
  const ref = useRef(null)
  const [nueva, setNueva] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // Text of the options while turning this column into estado; null = not doing that.
  const [aEstado, setAEstado] = useState(null)
  // The column's own name as typed in the menu.
  const [titulo, setTitulo] = useState(columna.nombre)
  const renombrarColumna = async () => {
    const nuevo = titulo.trim()
    if (!nuevo || nuevo === columna.nombre) { setTitulo(columna.nombre); return }
    // "Grupo" is what ties a bus to its group's ideal (idealLogica.COLUMNA_GRUPO).
    const aviso = columna.ambito !== 'partes' && columna.nombre === 'Grupo' ? `\n\n${t('unidades.planilla.renameGrupoWarning')}` : ''
    if (!confirm(t('unidades.planilla.renameColumnConfirm', { de: columna.nombre, a: nuevo }) + aviso)) return
    const ok = await run(() => onUpdate({ nombre: nuevo }))
    if (!ok) setTitulo(columna.nombre)
  }
  // The option being edited (name + color), if any, and its name as typed.
  const [pintando, setPintando] = useState(null)
  const [nombreNuevo, setNombreNuevo] = useState('')
  const abrirOpcion = (o) => {
    setPintando(p => p === o ? null : o)
    setNombreNuevo(o)
  }

  const renombrar = async () => {
    const de = pintando
    const a = nombreNuevo.trim()
    if (!a || a === de) return
    const otra = opciones.find(x => x !== de && x.toLowerCase() === a.toLowerCase())
    if (otra && !confirm(t('unidades.planilla.mergeConfirm', { de, a: otra }))) return
    if (!otra && !confirm(t('unidades.planilla.renameConfirm', { de, a }))) return
    const ok = await run(() => onRenombrar(de, a, !!otra))
    if (ok) { setPintando(otra || a); setNombreNuevo(otra || a) }
  }

  useEffect(() => {
    // A header button toggles the menu itself; closing here too would make
    // its click reopen it.
    const onDoc = (e) => {
      if (ref.current?.contains(e.target) || e.target.closest?.('[data-columna-btn]')) return
      onClose()
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    // Fixed position: any scroll or resize would leave it floating off its column.
    const onMove = (e) => { if (!ref.current?.contains(e.target)) onClose() }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onClose)
    window.addEventListener('scroll', onMove, true)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onClose)
      window.removeEventListener('scroll', onMove, true)
    }
  }, [onClose])

  const run = async (fn) => {
    setBusy(true); setError('')
    let ok = true
    try { await fn() } catch (err) {
      ok = false
      // The backend says why (e.g. "An estado column needs at least one option").
      const detail = err.response?.data?.detail
      setError(typeof detail === 'string' ? `${t('unidades.planilla.columnSaveFailed')} (${detail})`
        : t('unidades.planilla.columnSaveFailed'))
    }
    setBusy(false)
    return ok
  }

  const changeTipo = (tipo) => {
    if (tipo === columna.tipo) { setAEstado(null); return }
    if (tipo === 'estado') {
      // Ask for the options first, proposing what the column already holds.
      setAEstado((valoresEnUso.length ? valoresEnUso : [t('unidades.planilla.defaultOption')]).join('\n'))
      return
    }
    setAEstado(null)
    if (!confirm(t('unidades.planilla.changeTypeConfirm', { nombre: columna.nombre, tipo: t(`unidades.planilla.tipos.${tipo}`) }))) return
    run(() => onUpdate({ tipo }))
  }

  const confirmarEstado = async () => {
    // Empty is fine: the backend then keeps the held values or a default option.
    const ok = await run(() => onUpdate({ tipo: 'estado', opciones: parseOpciones(aEstado || '') }))
    if (ok) setAEstado(null)
  }

  const opciones = columna.opciones || []
  const addOpcion = () => {
    const o = nueva.trim()
    if (!o || opciones.some(x => x.toLowerCase() === o.toLowerCase())) { setNueva(''); return }
    run(async () => { await onUpdate({ opciones: [...opciones, o] }); setNueva('') })
  }
  const removeOpcion = (o) => {
    if (opciones.length === 1) { setError(t('unidades.planilla.optionsRequired')); return }
    if (pintando === o) setPintando(null)
    run(() => onUpdate({ opciones: opciones.filter(x => x !== o) }))
  }

  // color = null goes back to the automatic one.
  const pintar = (o, color) => {
    const colores = { ...(columna.colores || {}) }
    if (color) colores[o] = color
    else delete colores[o]
    run(() => onUpdate({ colores }))
  }

  const style = {
    position: 'fixed',
    top: anchor.bottom + 4,
    left: Math.max(8, Math.min(anchor.left, window.innerWidth - 288)),
    // With the color palette open it grows; scroll inside, never off-screen.
    maxHeight: Math.max(240, window.innerHeight - anchor.bottom - 16),
    overflowY: 'auto',
  }

  return (
    <div ref={ref} style={style}
      className="z-50 w-72 bg-white border border-gray-200 rounded-xl shadow-lg p-3 space-y-3 text-sm">
      {onOrdenar && (
        <div className={soloLectura ? '' : 'pb-3 border-b border-gray-100'}>
          <OrdenBotones tipo={columna.tipo}
            dir={orden && String(orden.campo) === String(columna.id) ? orden.dir : null}
            onOrdenar={d => { onOrdenar(columna.id, d); onClose() }} />
        </div>
      )}
      {!soloLectura && (<>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.planilla.columnName')}</label>
        <div className="flex gap-1.5 mb-1">
          <input className={`${inputCls} py-1.5 font-semibold`} value={titulo} disabled={busy}
            onChange={e => setTitulo(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); renombrarColumna() }
              else if (e.key === 'Escape') { e.stopPropagation(); setTitulo(columna.nombre) }
            }} />
          <button type="button" onClick={renombrarColumna}
            disabled={busy || !titulo.trim() || titulo.trim() === columna.nombre}
            className="px-3 rounded-lg bg-gray-900 text-white text-xs font-medium disabled:opacity-40 shrink-0">
            {t('unidades.planilla.rename')}
          </button>
        </div>
        <p className="text-xs text-gray-400">
          {columna.ambito === 'partes' ? t('partes.boardColumn') : t('unidades.planilla.projectOnly', { proyecto: columna.proyecto })}
        </p>
      </div>
      {columna.ambito === 'partes' && columna.tipo !== 'archivo' && (
        <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" className="mt-0.5 cursor-pointer" checked={!!columna.obligatoria} disabled={busy}
            onChange={e => run(() => onUpdate({ obligatoria: e.target.checked }))} />
          <span>
            <span className="font-medium">{t('partes.obligatoria')}</span>
            <span className="block text-[11px] text-gray-400">{t('partes.obligatoriaHint')}</span>
          </span>
        </label>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.planilla.columnType')}</label>
        <select className={inputCls} value={aEstado !== null ? 'estado' : columna.tipo} disabled={busy}
          onChange={e => changeTipo(e.target.value)}>
          {TIPOS.map(tp => <option key={tp} value={tp}>{TIPO_ICON[tp]}  {t(`unidades.planilla.tipos.${tp}`)}</option>)}
        </select>
      </div>
      {aEstado !== null && (
        <div className="rounded-lg bg-gray-50 border border-gray-100 p-2.5 space-y-2">
          <label className="block text-xs font-medium text-gray-700">{t('unidades.planilla.toEstadoTitle')}</label>
          <textarea rows={4} autoFocus className={inputCls} value={aEstado}
            placeholder={t('unidades.planilla.optionsPlaceholder')}
            onChange={e => setAEstado(e.target.value)} />
          <p className="text-[11px] text-gray-400">{t('unidades.planilla.toEstadoHint')}</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAEstado(null)} disabled={busy}
              className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50">
              {t('unidades.cancel')}
            </button>
            <button type="button" onClick={confirmarEstado} disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium disabled:opacity-50">
              {t('unidades.planilla.toEstadoConfirm')}
            </button>
          </div>
        </div>
      )}
      {columna.tipo === 'estado' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('unidades.planilla.options')}</label>
          <p className="text-[11px] text-gray-400 mb-1.5">{t('unidades.planilla.colorHint')}</p>
          <div className="flex flex-wrap gap-1.5 mb-2 max-h-40 overflow-y-auto">
            {opciones.map(o => (
              <span key={o} className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs ${
                COLORES[colorDe(columna, o)].pill} ${pintando === o ? 'ring-2 ring-gray-900 ring-offset-1' : ''}`}>
                <button type="button" disabled={busy} onClick={() => abrirOpcion(o)}
                  title={t('unidades.planilla.editOption')} className="max-w-[12rem] truncate">
                  {o}
                </button>
                <button type="button" disabled={busy} onClick={() => removeOpcion(o)}
                  className="w-4 h-4 rounded-full hover:bg-black/10 leading-none opacity-60"
                  title={t('unidades.planilla.removeOption')}>×</button>
              </span>
            ))}
          </div>
          {pintando && opciones.includes(pintando) && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-2 mb-2">
              <label className="block text-[11px] text-gray-500 mb-1">{t('unidades.planilla.optionName')}</label>
              <div className="flex gap-1.5 mb-1">
                <input className={`${inputCls} py-1.5`} value={nombreNuevo} disabled={busy}
                  onChange={e => setNombreNuevo(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); renombrar() }
                    else if (e.key === 'Escape') { e.stopPropagation(); setNombreNuevo(pintando) }
                  }} />
                <button type="button" onClick={renombrar}
                  disabled={busy || !nombreNuevo.trim() || nombreNuevo.trim() === pintando}
                  className="px-3 rounded-lg bg-gray-900 text-white text-xs font-medium disabled:opacity-40 shrink-0">
                  {t('unidades.planilla.rename')}
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mb-2">{t('unidades.planilla.renameHint')}</p>
              <p className="text-[11px] text-gray-500 mb-1.5 truncate">
                {t('unidades.planilla.colorFor', { opcion: pintando })}
              </p>
              <div className="grid grid-cols-7 gap-1.5">
                {Object.entries(COLORES).map(([nombre, c]) => {
                  const actual = colorDe(columna, pintando) === nombre
                  return (
                    <button key={nombre} type="button" disabled={busy} onClick={() => pintar(pintando, nombre)}
                      title={t(`unidades.planilla.colores.${nombre}`)} aria-label={t(`unidades.planilla.colores.${nombre}`)}
                      className={`w-7 h-7 rounded-full ${c.dot} flex items-center justify-center text-white text-xs transition-transform hover:scale-110 ${
                        actual ? 'ring-2 ring-offset-1 ring-gray-900' : ''}`}>
                      {actual ? '✓' : ''}
                    </button>
                  )
                })}
              </div>
              {columna.colores?.[pintando] && (
                <button type="button" disabled={busy} onClick={() => pintar(pintando, null)}
                  className="mt-2 text-[11px] font-medium text-gray-500 hover:text-gray-900">
                  {t('unidades.planilla.autoColor')}
                </button>
              )}
            </div>
          )}
          <div className="flex gap-1.5">
            <input className={`${inputCls} py-1.5`} value={nueva} placeholder={t('unidades.planilla.addOptionPlaceholder')}
              onChange={e => setNueva(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOpcion() } }} />
            <button type="button" disabled={busy || !nueva.trim()} onClick={addOpcion}
              className="px-3 rounded-lg bg-gray-900 text-white text-xs font-medium disabled:opacity-40">+</button>
          </div>
        </div>
      )}
      {error && <p className="text-red-500 text-xs">{error}</p>}
      <div className="pt-2 border-t border-gray-100">
        <button type="button" disabled={busy} onClick={onDelete}
          className="text-red-600 hover:text-red-700 text-xs font-medium">
          {t('unidades.planilla.deleteColumn')}
        </button>
      </div>
      </>)}
    </div>
  )
}
